import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { audience } from '@bb/contracts';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi, type AudienceReach } from '../../api/client.ts';
import { ChannelReachBreakdown, formatCount } from './ChannelReachBreakdown.tsx';

interface Props {
  environmentId: string;
  definition: audience.AudienceDefinition;
  /** Debounce of the estimate while the audience is edited. */
  debounceMs?: number;
  /** Poll interval of exact statistics. */
  pollMs?: number;
}

const timeOf = (iso: string) => `${iso.slice(11, 16)} UTC`;

function Funnel({ reach, definition }: { reach: AudienceReach; definition: audience.AudienceDefinition }) {
  if (reach.funnel.length < 2) return null;
  return (
    <ol aria-label="Users after each group">
      {reach.funnel.map((step, i) => (
        <li key={step.id}>
          {step.label ?? `Group ${i + 1}`}: {formatCount(step.count)}
        </li>
      ))}
      {(definition.exclude.length > 0 || definition.apps.mode !== 'all') && <li>After exclusions and apps: {formatCount(reach.count)}</li>}
    </ol>
  );
}

/**
 * Size of an audience (BB-44): the quick estimate («N ± M», exact in small environments), its share of the environment
 * and its time, reach by channel, users left after each group, and exact statistics on demand (queued, polled,
 * cancellable).
 */
export function AudiencePopulation({ environmentId, definition, debounceMs = 500, pollMs = 1_000 }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [current, setCurrent] = useState(definition);
  useEffect(() => {
    const timer = setTimeout(() => setCurrent(definition), debounceMs);
    return () => clearTimeout(timer);
  }, [definition, debounceMs]);
  const estimate = useQuery({ queryKey: ['audience-estimate', environmentId, current], queryFn: () => api.estimateAudience(environmentId, current), retry: false });
  const [exactFor, setExactFor] = useState<{ id: string; definition: audience.AudienceDefinition } | null>(null);
  const requestExact = useMutation({ mutationFn: () => api.requestExactAudience(environmentId, current), onSuccess: (r) => setExactFor({ id: r.id, definition: current }) });
  const exactId = exactFor && exactFor.definition === current ? exactFor.id : null;
  const exact = useQuery({
    queryKey: ['audience-exact', environmentId, exactId],
    queryFn: () => api.getExactAudience(environmentId, exactId!),
    enabled: exactId !== null,
    refetchInterval: (query) => (query.state.data && (query.state.data.status === 'queued' || query.state.data.status === 'running') ? pollMs : false),
  });
  const cancel = useMutation({ mutationFn: () => api.cancelExactAudience(environmentId, exactId!), onSuccess: () => exact.refetch() });

  if (estimate.error) {
    const invalid = estimate.error instanceof ApiError && estimate.error.code === 'invalid_audience';
    return (
      <section aria-label="Audience size" className="audience-population">
        <h3>Target population</h3>
        <p role="status">{invalid ? 'Fix the audience to see its size.' : 'The size could not be estimated.'}</p>
      </section>
    );
  }
  const reach = estimate.data;
  const status = exact.data?.status;
  const exactResult = status === 'completed' ? exact.data!.result : null;
  const shown = exactResult ?? reach;

  return (
    <section aria-label="Audience size" className="audience-population">
      <h3>Target population</h3>
      {!reach ? (
        <p role="status">Estimating…</p>
      ) : (
        <>
          <p>
            <strong>{reach.mode === 'estimate' ? 'Estimated users' : 'Users'}</strong> {formatCount(reach.count)}
            {reach.mode === 'estimate' && ` ± ${formatCount(reach.margin)}`}
          </p>
          <p>
            {reach.percentOfEnvironment}% of the environment ({formatCount(reach.population)} users)
          </p>
          <p className="hint">
            As of {timeOf(reach.computedAt)}
            {reach.cacheHit ? ' (cached)' : ''} ·{' '}
            {reach.sample ? `estimated from a ${reach.sample.buckets / 100}% sample of ${formatCount(reach.sample.profiles)} users; 95% interval ${formatCount(reach.low)}–${formatCount(reach.high)}` : 'counted exactly'}
          </p>
          {shown && <ChannelReachBreakdown reachable={shown.reachable} estimated={shown.mode === 'estimate'} />}
          {shown && <Funnel reach={shown} definition={current} />}
          {reach.mode === 'estimate' && (
            <div className="audience-population__exact">
              {exactResult ? (
                <p role="status">
                  Exact: {formatCount(exactResult.count)} users (as of {timeOf(exactResult.computedAt)}){exact.data!.stale ? ' · the data has changed since' : ''}
                </p>
              ) : status === 'queued' || status === 'running' ? (
                <p role="status">
                  {status === 'queued' ? 'Waiting in the queue…' : 'Calculating exact statistics…'}{' '}
                  <Button size="sm" variant="ghost" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                    Cancel
                  </Button>
                </p>
              ) : (
                <>
                  {status === 'cancelled' && <p role="status">Exact statistics cancelled.</p>}
                  {status === 'failed' && <p role="alert">Exact statistics failed: {exact.data!.error}</p>}
                  <Button size="sm" onClick={() => requestExact.mutate()} disabled={requestExact.isPending}>
                    Calculate exact statistics
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
